import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch
} from "firebase/firestore"
import { httpsCallable } from "firebase/functions"
import { db, functions } from "@/config/firebase"
import { CustomPrompt, Prompt } from "@/shared/types/prompt"
import { buildCustomPromptSelectionId, MAX_CUSTOM_PROMPT_LENGTH } from "@/shared/utils/promptIds"

interface PromptUpdateSummary {
  total: number
  written: number
  ids: string[]
}

interface GeneratePromptInput {
  name: string
  id: number | string
  exampleUrl: string
  categoryName: string
}

interface GeneratePromptSummary {
  total: number
  succeeded: number
  failed: number
}

type CustomPromptListener = (prompts: CustomPrompt[]) => void
type CustomPromptErrorListener = (error: Error) => void

const toPrompt = (docData: any, docId: string): Prompt => ({
  id: String(docData?.id ?? docId),
  name: String(docData?.name ?? ""),
  exampleUrl: String(docData?.exampleUrl ?? ""),
  categoryName: docData?.categoryName,
  source: "global"
})

const toCustomPrompt = (docData: any, docId: string, ownerId: string): CustomPrompt => ({
  id: buildCustomPromptSelectionId(String(docData?.id ?? docId)),
  name: String(docData?.name ?? ""),
  exampleUrl: String(docData?.exampleUrl ?? ""),
  source: "custom",
  ownerId,
  createdAt: docData?.createdAt,
  updatedAt: docData?.updatedAt
})

const fetchPromptCatalog = async (): Promise<Prompt[]> => {
  const promptsSnap = await getDocs(query(collection(db, "prompts")))
  const prompts = promptsSnap.docs.map(promptDoc => toPrompt(promptDoc.data(), promptDoc.id))
  return prompts
}

const subscribeToCustomPrompts = (
  userId: string,
  onUpdate: CustomPromptListener,
  onError?: CustomPromptErrorListener
): (() => void) => {
  const q = query(collection(db, "users", userId, "custom_prompts"), orderBy("updatedAt", "desc"))

  return onSnapshot(
    q,
    snapshot => {
      onUpdate(snapshot.docs.map(customPromptDoc => toCustomPrompt(customPromptDoc.data(), customPromptDoc.id, userId)))
    },
    error => {
      console.error("Error loading custom prompts:", error)
      onError?.(error)
    }
  )
}

const saveCustomPrompt = async (userId: string, promptText: string): Promise<CustomPrompt> => {
  const name = String(promptText || "")
    .trim()
    .slice(0, MAX_CUSTOM_PROMPT_LENGTH)
  if (!name) {
    throw new Error("Prompt text is required.")
  }

  const customPromptRef = doc(collection(db, "users", userId, "custom_prompts"))
  const customPrompt = {
    id: customPromptRef.id,
    name,
    exampleUrl: "",
    ownerId: userId,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }

  await setDoc(customPromptRef, customPrompt)
  return toCustomPrompt(customPrompt, customPromptRef.id, userId)
}

const deleteCustomPrompt = async (userId: string, promptId: string): Promise<void> => {
  await deleteDoc(doc(db, "users", userId, "custom_prompts", promptId))
}

const updatePrompts = async (prompts: Prompt[]): Promise<PromptUpdateSummary> => {
  if (!Array.isArray(prompts) || prompts.length === 0) {
    return { total: 0, written: 0, ids: [] }
  }

  const batch = writeBatch(db)
  const ids: string[] = []

  prompts.forEach(prompt => {
    const id = String(prompt.id || "").trim()
    if (!id) {
      throw new Error("Each prompt must include a non-empty id.")
    }

    const ref = doc(db, "prompts", id)
    batch.set(
      ref,
      {
        id,
        name: String(prompt.name || ""),
        exampleUrl: String(prompt.exampleUrl || ""),
        categoryName: String(prompt.categoryName || "")
      },
      { merge: true }
    )

    ids.push(id)
  })

  await batch.commit()
  return {
    total: prompts.length,
    written: prompts.length,
    ids
  }
}

const generatePrompt = async (prompts: GeneratePromptInput[]): Promise<GeneratePromptSummary> => {
  if (!Array.isArray(prompts) || prompts.length === 0) {
    throw new Error("Prompts array is required.")
  }

  const generatePromptCallable = httpsCallable<GeneratePromptInput[], GeneratePromptSummary>(
    functions,
    "generatePrompt"
  )
  const result = await generatePromptCallable(prompts)
  return result.data
}

export { fetchPromptCatalog }
export { saveCustomPrompt, deleteCustomPrompt, subscribeToCustomPrompts }
export { updatePrompts, generatePrompt }
export type { PromptUpdateSummary, GeneratePromptInput, GeneratePromptSummary }
